import type { GroupCallLifecycleState } from '@domain/realtime/types';
import { requestJson } from '@shared/api/http';

type ApiEnvelope<T> = {
  data?: T;
  status?: string;
  message?: string;
};

export type LiveKitTokenRequest = {
  mediaCapability: string;
  deviceId?: string;
  platform?: string;
  displayName?: string;
};

export type LiveKitTokenResponse = {
  livekitUrl: string;
  roomName: string;
  token: string;
  identity: string;
  participantName: string;
  expiresIn: number;
};

function resolveLiveKitUrl(url: string) {
  if (!url || url === '/livekit') {
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${scheme}//${window.location.host}/livekit`;
  }
  return url;
}

export async function fetchLiveKitToken(
  apiBase: string,
  params: LiveKitTokenRequest,
): Promise<LiveKitTokenResponse> {
  const suffix = new URLSearchParams({
    platform: params.platform || 'web',
    ...(params.deviceId ? { deviceId: params.deviceId } : {}),
  }).toString();
  const response = await requestJson<ApiEnvelope<LiveKitTokenResponse>>(
    `/call/livekit-token?${suffix}`,
    {
      baseUrl: apiBase,
      method: 'POST',
      body: JSON.stringify({
        mediaCapability: params.mediaCapability,
        displayName: params.displayName,
      }),
    },
  );
  const data = response.data ?? (response as LiveKitTokenResponse);
  return {
    ...data,
    livekitUrl: resolveLiveKitUrl(data.livekitUrl),
  };
}

type GroupLifecycleEnvelope = ApiEnvelope<GroupCallLifecycleState> & GroupCallLifecycleState;

function lifecycleData(response: GroupLifecycleEnvelope): GroupCallLifecycleState {
  const data = response.data ?? response;
  if (!data.callId || !data.sessionId || !data.attendanceLegId || !data.mediaCapability) {
    throw new Error('群组通话服务返回了无效的准入结果');
  }
  return data;
}

async function lifecycleCommand(
  apiBase: string,
  operation: 'start' | 'join',
  body: Record<string, unknown>,
) {
  return lifecycleData(await requestJson<GroupLifecycleEnvelope>(`/call/group/${operation}`, {
    baseUrl: apiBase,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

export function startGroupCall(
  apiBase: string,
  params: { commandId: string; groupId: number; mediaKind: 'audio' | 'video'; deviceId: string },
) {
  return lifecycleCommand(apiBase, 'start', params);
}

export function joinGroupCall(
  apiBase: string,
  params: { commandId: string; callId: string; sessionId: string; deviceId: string },
) {
  return lifecycleCommand(apiBase, 'join', params);
}

export async function closeGroupCall(
  apiBase: string,
  operation: 'leave' | 'end',
  params: {
    commandId: string;
    callId: string;
    sessionId: string;
    attendanceLegId: string;
    fence: number;
  },
) {
  await requestJson(`/call/group/${operation}`, {
    baseUrl: apiBase,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}
